import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  InputGroup,
  InputRightElement,
  NumberInput,
  NumberInputField,
  Spinner,
} from "@chakra-ui/react";
import { Icons, Typography } from "@reconvert/react-ui-component";
import { useAppSelector } from "../../../app/hooks";
import { selectAppState } from "../../../app/slice/appSlice";
import useVariants from "../../../hooks/useVariants";
import useVariantAnalytics from "../../../hooks/useVariantAnalytics";
import useUnsavedBar from "../../../hooks/useUnsavedBar";
import useIsActiveOnPage, { PagePurpose } from "../../../hooks/useIsActiveOnPage";
import { Slot } from "../../../models";
import { prepareVariantAnalyticsRow } from "./AnalyticView";
import AnalyticsRow from "./AnalyticsRow";
import VariantAnalytics from "../../CheckoutPage/components/VariantAnalytics";
import { useTranslation } from "react-i18next";
import { useCentralConfirmModal } from "../../../context/ConfirmModalContext";

const MobileVariantTabs = ({ slot, slotVariants }: { slot: Slot; slotVariants: any[] }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const getVariantLetter = (index: number) => String.fromCharCode(65 + index);

  const tabFlex =
    slotVariants.length < 4 ? "1 1 0" : "0 1 25%";

  return (
    <Box mt={3}>
      <Divider mb={3} />

      {slot?.isABTestingEnabled && (
        <Flex
          w="100%"
          flexWrap="wrap"
          borderRadius="8px"
          overflow="hidden"
          border="1px solid"
          borderColor="neutrals.300"
        >
          {slotVariants.map((v: any, index: number) => (
            <Box
              key={v.id}
              flex={tabFlex}
              minW={0}
              textAlign="center"
              py="8px"
              px="8px"
              cursor="pointer"
              bg={activeTabIndex === index ? "primary.500" : "neutrals.100"}
              color={activeTabIndex === index ? "white" : "neutrals.700"}
              fontWeight={500}
              fontSize="14px"
              onClick={() => setActiveTabIndex(index)}
              borderRadius={activeTabIndex === index ? "8px" : "0"}
            >
              {getVariantLetter(index)} ({v.abImpressionShare}%)
            </Box>
          ))}
        </Flex>
      )}

      <MobileVariantTabContent
        variant={slotVariants[activeTabIndex]}
        variantLetter={getVariantLetter(activeTabIndex)}
        slot={slot}
      />
    </Box>
  );
};

const MobileVariantTabContent = ({
  variant,
  variantLetter,
  slot,
}: {
  variant: any;
  variantLetter: string;
  slot: Slot;
}) => {
  const { t } = useTranslation();
  const { navigateToEditor, handleUpdateVariantImpressionShare, handleDeleteVariantId, deletingVariant, createABVariant, creatingVariant } = useVariants();
  const { preventAction } = useUnsavedBar();
  const { openConfirmModal, setConfirmLoading } = useCentralConfirmModal();

  const handleOpenDeleteModal = useCallback(() => {
    openConfirmModal({
      title: t("Are you sure you want to delete {{name}}?", { name: t(variant?.name) }),
      okButtonText: t("Delete variant"),
      cancelButtonText: t("Cancel"),
      onConfirm: () => handleDeleteVariantId(variant?.id),
      onCancel: () => { },
      destructive: true,
    });
  }, [openConfirmModal, t, variant?.name, variant?.id, handleDeleteVariantId]);

  useEffect(() => {
    setConfirmLoading(deletingVariant);
  }, [deletingVariant, setConfirmLoading]);
  const {
    variantSummaryAnalyticData,
    loading: loadingSummary,
    fetchAnalytics: fetchSummaryAnalytics,
  } = useVariantAnalytics();
  const { variantAnalyticData, loading: loadingWidgets, fetchAnalytics: fetchWidgetAnalytics } = useVariantAnalytics();
  const {
    dateRange,
    view: { isAnalyticView },
    activePage,
  } = useAppSelector(selectAppState);
  const isAnalyticsDeclineOffers = useIsActiveOnPage(PagePurpose.ANALYTICS_DECLINE_OFFERS, activePage!);

  const [abImpressionShare, setAbImpressionShare] = useState(variant?.abImpressionShare);
  const previousAbImpressionShareRef = useRef(abImpressionShare);
  const [widgetAnalyticsOpen, setWidgetAnalyticsOpen] = useState(false);

  useEffect(() => {
    setAbImpressionShare(variant?.abImpressionShare);
  }, [variant?.id, variant?.abImpressionShare]);

  useEffect(() => {
    if (variant?.id && isAnalyticView) {
      fetchSummaryAnalytics(variant.id, "SLOT_VARIANT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, isAnalyticView, variant?.id]);

  useEffect(() => {
    if (variant?.id && isAnalyticView && widgetAnalyticsOpen) {
      fetchWidgetAnalytics(variant.id, "WIDGET");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, isAnalyticView, widgetAnalyticsOpen, variant?.id]);

  const analyticsDetails = useMemo(() => {
    if (variantSummaryAnalyticData[0]) {
      return prepareVariantAnalyticsRow(t, isAnalyticsDeclineOffers, variantSummaryAnalyticData[0]);
    }

    return null;
  }, [variantSummaryAnalyticData, isAnalyticsDeclineOffers, t]);

  return (
    <Box mt={4}>
      {slot?.isABTestingEnabled && (
        <Flex justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="subheading2" fontWeight={600} textTransform="uppercase" color="neutrals.900">
            {t("Variant")} {variantLetter}
          </Typography>
          <Flex alignItems="center" gap={3}>
            <Typography
              variant="body"
              color="link.color"
              fontWeight={500}
              cursor="pointer"
              onClick={() => preventAction(() => navigateToEditor(variant?.id, slot?.id, slot?.slotNo))}
            >
              {t("Edit Variant")}
            </Typography>
            <Icons.TrashSimple
              color="error.700"
              height="16px"
              width="16px"
              cursor="pointer"
              onClick={() => preventAction(handleOpenDeleteModal)}
            />
          </Flex>
        </Flex>
      )}

      {slot?.isABTestingEnabled && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          py="8px"
          px="12px"
          borderRadius="8px"
          border="1px solid"
          borderColor="neutrals.300"
          bg="white"
          mb={3}
        >
          <Typography variant="body" color="neutrals.900" fontWeight={400}>
            {t("Impression share")}
          </Typography>
          <InputGroup maxW="74px">
            <NumberInput
              value={abImpressionShare}
              max={100}
              min={0}
              onChange={val => {
                const numericVal = parseInt(val, 10);

                if (numericVal > 100 || numericVal < 0) return;

                setAbImpressionShare(isNaN(numericVal) ? 0 : numericVal);
              }}
              onBlur={() => {
                if (previousAbImpressionShareRef.current !== abImpressionShare) {
                  handleUpdateVariantImpressionShare(variant?.id, variant?.abImpressionShare, abImpressionShare);
                  previousAbImpressionShareRef.current = abImpressionShare;
                }
              }}
            >
              <NumberInputField color="#1A202C" />
            </NumberInput>
            <InputRightElement>%</InputRightElement>
          </InputGroup>
        </Flex>
      )}

      {slot?.isABTestingEnabled && (
        <>
          {loadingSummary ? (
            <Flex justifyContent="center" py={3}>
              <Spinner size="sm" />
            </Flex>
          ) : (
            <Flex flexDirection="column">
              {analyticsDetails?.map((val: any, index: number) => (
                <AnalyticsRow
                  key={index}
                  name={val.name}
                  value={val.currentValue}
                  isForSidebar
                  change={val.diffInPercentage}
                  variantStatus={variant?.status}
                  iconSize="sm"
                  type={val.type}
                />
              ))}
            </Flex>
          )}
        </>
      )}

      {slot?.isABTestingEnabled && <Divider mt={3} />}
      <Flex
        mt={2}
        py={1}
        justifyContent="space-between"
        alignItems="center"
        cursor="pointer"
        onClick={() => setWidgetAnalyticsOpen(prev => !prev)}
        userSelect="none"
      >
        <Typography variant="subheading2" fontWeight={500} color="neutrals.800">
          {t("Widget analytics")}
        </Typography>
        {widgetAnalyticsOpen ? (
          <Icons.CaretUp height="16px" width="16px" color="neutrals.700" />
        ) : (
          <Icons.ChevronDown height="16px" width="16px" color="neutrals.700" />
        )}
      </Flex>
      <Collapse in={widgetAnalyticsOpen} animateOpacity>
        {loadingWidgets ? (
          <Flex justifyContent="center" py={4}>
            <Spinner size="sm" />
          </Flex>
        ) : (
          <>
            <VariantAnalytics variantAnalyticData={variantAnalyticData} />
            <Flex w="100%" mt={3}>
              <Button
                variant="secondary"
                size={"sm"}
                fontSize={16}
                px={4}
                fontWeight={500}
                disabled={creatingVariant}
                isLoading={creatingVariant}
                flexGrow={1}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  preventAction(() => createABVariant())
                }}
              >
                {slot?.isABTestingEnabled ? t("Add variant") : t("Create A/B test")}
              </Button>
            </Flex></>
        )}
      </Collapse>
    </Box>
  );
};

export default MobileVariantTabs;
